import { useMemo } from 'react';
import { SxProps, Theme } from '@mui/material';
import { useDesignerMode } from '../Contexts/DesignerModeContext';
import { getEditModeClasses, isComponentSelected } from '../Utils/editModeUtils';
import { IComponent } from '../Models/IComponent';
import { IsDesignMode } from '../Utils/runtimeDetection';
import {
    REFERENCE_SELECTION_COLOR,
    REFERENCE_SELECTION_COLOR_BG,
    REFERENCE_SELECTION_COLOR_HOVER_BG,
    REFERENCE_SELECTION_COLOR_LIGHT,
    REFERENCE_SELECTION_COLOR_MEDIUM,
    SELECTION_BORDER_RADIUS,
    SELECTION_PADDING
} from '../Constants/DesignerModeStyles';

interface UseComponentSelectionOptions {
    component: IComponent;
    componentPath?: string[];
    isContainer?: boolean;
    isEmpty?: boolean;
    /** Additional data attributes to add (e.g., for ReferenceComponent) */
    additionalDataAttributes?: Record<string, string>;
    /** Style variant for special component types */
    variant?: 'default' | 'reference';
}

const EMPTY_SELECTION_PROPS: ComponentSelectionProps = {};

interface ComponentSelectionProps {
    className?: string;
    sx?: SxProps<Theme>;
    'data-component-id'?: string;
    'data-component-type'?: string;
    'data-component-path'?: string;
    'data-is-container'?: string;
    [key: string]: any; // Allow additional data attributes
}

/**
 * Hook that provides props for making a component selectable in designer mode.
 * Returns data attributes and CSS classes for Ctrl+Click selection.
 *
 * Usage:
 * ```tsx
 * function MyComponent({ component, componentPath }) {
 *   const selectionProps = useComponentSelection({ component, componentPath });
 *
 *   return <Box {...selectionProps} sx={...}>
 *     ...
 *   </Box>
 * }
 * ```
 */
export function useComponentSelection({
    component,
    componentPath = [],
    isContainer = false,
    isEmpty = false,
    additionalDataAttributes = {},
    variant = 'default',
}: UseComponentSelectionOptions): ComponentSelectionProps {
    // In runtime mode, short-circuit to avoid hook overhead
    // In design mode, always run hooks because component selection is needed
    if (!IsDesignMode) {
        return EMPTY_SELECTION_PROPS;
    }

    // Note: These hooks are placed in this condition on purpose to avoid unnecessary evaluation
    // IsDesignMode is constant during the lifecycle of the component, so it's safe to call hooks conditionally

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const designerMode = useDesignerMode();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(() => {
        if (!designerMode) {
            return {};
        }

        // Check if this component is currently selected
        const selected = isComponentSelected(component.Id, designerMode.selectedPath ?? null, componentPath);

        // Build CSS classes for designer mode styling
        const className = getEditModeClasses({
            editMode: true,
            isSelected: selected,
            isContainer,
            isEmpty,
        });

        // Build base props
        const baseProps: ComponentSelectionProps = {
            className,
            'data-component-id': component.Id,
            'data-component-type': component.Type,
            'data-component-path': JSON.stringify(componentPath),
            'data-is-container': isContainer ? 'true' : undefined,
            ...additionalDataAttributes,
        };

        // Add variant-specific styles
        if (variant === 'reference') {
            baseProps.sx = [
                {
                    position: 'relative',
                    padding: SELECTION_PADDING,
                    border: `1px dashed ${REFERENCE_SELECTION_COLOR_LIGHT}`,
                    borderRadius: SELECTION_BORDER_RADIUS,
                    '&:hover': {
                        borderColor: REFERENCE_SELECTION_COLOR_MEDIUM,
                        backgroundColor: REFERENCE_SELECTION_COLOR_HOVER_BG,
                    },
                    '&.iw-selected': {
                        borderColor: REFERENCE_SELECTION_COLOR,
                        backgroundColor: REFERENCE_SELECTION_COLOR_BG,
                    }
                }
            ];
        }

        return baseProps;
    }, [designerMode, component.Id, component.Type, componentPath, isContainer, isEmpty, additionalDataAttributes, variant]);
}

/**
 * Gets just the visual container classes without data attributes.
 * Use this for the actual child container element when it's separate from the root.
 */
export function getContainerClasses(isInDesignerMode: boolean, isEmpty: boolean): string {
    if (!isInDesignerMode) return '';

    return getEditModeClasses({
        editMode: true,
        isSelected: false, // Visual container doesn't show selection
        isContainer: true,
        isEmpty,
    });
}

/**
 * Returns data attributes to link portaled content (Popover, Dialog, Menu, Drawer, etc.) back to its parent component.
 * This enables the designer mode click handler to traverse the portal boundary and find SharedComponent references.
 * 
 * **When to use:** Apply to any component that renders children inside a React Portal (MUI components like
 * Popover, Dialog, Menu, Drawer, Tooltip, etc.). The attributes should be applied to the Paper/Container
 * element inside the portal, not the portal root.
 * 
 * **Components that need this:**
 * - GlobalSearch (Popover with children)
 * - ViewComponent (Dialog with children)
 * - Any custom component using Portal, Popover, Dialog, Menu, Drawer, or Tooltip that renders dynamic children
 * 
 * @example
 * // Popover example (GlobalSearch)
 * ```tsx
 * <Popover>
 *   <Paper {...getPortalParentAttributes(component.Id, componentPath)}>
 *     {children}
 *   </Paper>
 * </Popover>
 * ```
 * 
 * @example
 * // Dialog example (ViewComponent)
 * ```tsx
 * <Dialog>
 *   <DialogContent {...getPortalParentAttributes(component.Id, componentPath)}>
 *     {children}
 *   </DialogContent>
 * </Dialog>
 * ```
 */
export function getPortalParentAttributes(
    componentId: string,
    componentPath: string[]
): Record<string, string> | undefined {
    if (!IsDesignMode) {
        return undefined;
    }

    return {
        'data-portal-parent-id': componentId,
        'data-portal-parent-path': JSON.stringify(componentPath),
    };
}
